import PosPlatform from '@b2bsoft/pos-platform';
import { traceContext, TraceContext } from '@b2bsoft/tracecontext';
import { observable, action, makeObservable } from 'mobx';

import LoaderModel from './shared/LoaderModel';
import { http, setupAPI } from 'webPosApp/api';
import ConfigurationModel from './shared/ConfigurationModel';
import WizardModel from './kiosk/WizardModel';
import { Billpay } from './kiosk/Billpay';
import { IWebPosSdk } from 'shared/interfaces/webPosSdk/IWebPosSdk';
import { Confirmation } from './kiosk/Confirmation';

export class StoreModel {
    public posSdk: IWebPosSdk;

    @observable public loader: LoaderModel;

    @observable public configuration: ConfigurationModel;

    @observable public traceContext: TraceContext;

    @observable billpay: Billpay;
    @observable wizard: WizardModel;
    @observable confirmation: Confirmation;

    @observable public locationId: number | null = null;
    @observable public carrierB2BId: string | null = null;
    @observable public brand: string | null = null;
    @observable public deviceId: string | null = null;
    @observable public accessCode: string | null = null;
    @observable public appId: string | null = null;
    @observable public sku: string | null = null;
    @observable public description: string | null = null;
    @observable public serviceCode: string | null = null;
    @observable public paymentApiUrl: string | null = null;

    @observable public isReady: boolean = false;

    @observable public language: string | null = null;

    /**
     * It's a feature that will allow our QA to manually input response code for all API requests
     * This is needed for better QA experience, to fairly test all cases/responses from API
     * @type {boolean}
     * @memberof StoreModel
     */
    public isQA: boolean = false;

    constructor() {
        this.loader = new LoaderModel();
        this.configuration = new ConfigurationModel();

        this.billpay = new Billpay(this);
        this.confirmation = new Confirmation(this);

        this.wizard = new WizardModel(this);

        makeObservable(this);
    }

    @action
    setValue(data: any) {
        Object.assign(this, data);
    }

    async init() {
        this.traceContext = traceContext;

        const posSdk = new PosPlatform('webPos', {
            webPosSalesSdkUrl: 'empty',
            webPosSalesSdkTimeout: 10000,
        });

        await posSdk.waitInit();
        this.posSdk = (await posSdk.getSsdkModule(true)).webPos as IWebPosSdk;

        await this.login();
        await this.configuration.init();
        this.wizard && this.wizard.init();
    }

    @action setReady() {
        this.isReady = true;
    }

    async login() {
        const envInfoResult = await this.posSdk.getEnvironmentInfo();
        if (!envInfoResult.isSuccess) {
            throw new Error(
                envInfoResult.errorMessage ?? 'Error getting EnvironmentInfo'
            );
        }
        const envInfo = envInfoResult.postData.data;

        this.setValue({
            brand: 'Optimum',
            deviceId: envInfo.deviceId,
            language: 'en',
            isQA: false,
            locationId: parseInt(envInfo.storeId),
            carrierB2BId: envInfo.carrierB2BId,
        });

        this.configuration.setValue({
            locationId: this.locationId,
            carrierB2BId: this.carrierB2BId,
        });

        setupAPI(this, {
            isQA: this.isQA,
            brand: this.brand,
            deviceId: this.deviceId,
            locationId: this.locationId,
            carrierB2BId: this.carrierB2BId,
            registerId: envInfo.registerId,
        });

        await http.get('/api/account/login').then((response: any) => {
            this.setValue({
                appId: response.data.appId,
                sku: response.data.sku,
                description: response.data.description,
                serviceCode: response.data.serviceCode,
                paymentApiUrl: response.data.amdocsApi,
            });
        });
    }
}

export default StoreModel;

export const Store = new StoreModel();
